import React, { useEffect, useState } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { CircularProgress, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import { v2Validate } from 'src/store/modules/auth/v2Operations';
import { getAppUrlForReferrer, getReferrerInfo } from 'src/utils/ssoReferrer';

const APP_REDIRECT_PRIORITY = ['atlas', 'beacon', 'advisor'];

const CenteredContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
  gap: '16px'
});

const NoAccessRedirect: React.FC<RouteComponentProps> = ({ history }) => {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const resolve = async () => {
      const params = new URLSearchParams(window.location.search);
      const deniedApp = (params.get('app') || 'atlas').toLowerCase();
      const divisionName = params.get('divName') || 'your division';

      try {
        const response = await v2Validate();

        if (!response.authenticated) {
          history.push('/sign_in');
          return;
        }

        const appAccess = response.division?.appAccess;
        const accessibleApps = appAccess
          ? Object.entries(appAccess)
              .filter(([name, level]) => name !== deniedApp && level && level !== 'blocked')
              .map(([name]) => name)
          : [];

        const targetApp =
          APP_REDIRECT_PRIORITY.find((a) => accessibleApps.includes(a)) || accessibleApps[0];

        if (targetApp) {
          const { stage } = getReferrerInfo();
          const referrer = stage === 'prod' ? targetApp : `${targetApp}-${stage}`;
          const targetBaseUrl = getAppUrlForReferrer(referrer);
          const sep = targetBaseUrl.includes('?') ? '&' : '?';
          const redirectParams = `sl_redirect=1&sl_from=${encodeURIComponent(deniedApp)}&sl_div=${encodeURIComponent(divisionName)}`;
          window.location.href = `${targetBaseUrl}${sep}${redirectParams}`;
        } else {
          window.location.href = `/switch-division?app=${encodeURIComponent(deniedApp)}`;
        }
      } catch (err: any) {
        if (err.response?.status === 401) {
          history.push('/sign_in');
        } else {
          setError('Something went wrong. Redirecting...');
          setTimeout(() => {
            window.location.href = `/switch-division?app=${encodeURIComponent(deniedApp)}`;
          }, 1500);
        }
      }
    };

    resolve();
  }, [history]);

  return (
    <CenteredContainer>
      <CircularProgress size={48} />
      <Typography variant="body1" color="text.secondary">
        {error || 'Checking access...'}
      </Typography>
    </CenteredContainer>
  );
};

export default withRouter(NoAccessRedirect);
